import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation,
  DOCUMENT,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';

import {
  ActivatedRoute,
  Event,
  NavigationEnd,
  NavigationStart,
  Params,
  Router,
  RouterOutlet,
} from '@angular/router';
import { combineLatest, distinctUntilChanged, map, startWith, Subject, takeUntil } from 'rxjs';
import { AppConfig, Scheme, Theme } from '../core/config/app.config';
import { Layout } from './layout';
import { ConfigService } from '../core/config/config.service';
import { MediaWatcherService } from '../core/media-watcher/media-watcher.service';
import { PlatformService } from '../core/platform/platform.service';
import { AdminLayoutComponent } from './layouts/admin/admin.component';
import { LoadingBarComponent } from '../shared/components/loading-bar/loading-bar.component';
import { LoadingBarService } from '../shared/components/loading-bar/loading-bar.service';
import { AuthService } from '../core/auth/auth.service';
import { BusinessUnitService } from '../modules/master-data/business-unit/business-unit.service';
import { TenantService } from '../modules/administration/tenant/tenant.service';
import { ITenant } from '../modules/administration/tenant/tenant';
import { IBusinessUnit } from '../modules/master-data/business-unit/business-unit';
import { FacilityService } from '../modules/master-data/facility/facility.service';
import { IFacility } from '../modules/master-data/facility/facility';
import { ContextSelectorService } from '../core/services/context-selector.service';
import { UserService } from '../core/user/user.service';
import { AuthStateService } from '../core/auth/auth-state.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'layout',
  imports: [RouterOutlet, AdminLayoutComponent, LoadingBarComponent, TranslateModule],
  templateUrl: './layout.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class LayoutComponent implements OnInit, OnDestroy {
  config!: AppConfig;
  layout!: Layout;
  scheme!: Scheme;
  theme!: Theme;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private readonly _authState = inject(AuthStateService);
  // Degraded-session banner (Identity & Access review 2026-07-27): transient refresh
  // failures preserve credentials, but the user must SEE that requests are failing.
  readonly sessionDegraded = this._authState.sessionDegraded;
  tenantId$ = this._activatedRoute.queryParams.pipe(
    map((params: Params) => {
      return params['tenantId'];
    }),
    distinctUntilChanged(),
  );
  businessUnitId$ = this._activatedRoute.queryParams.pipe(
    map((params: Params) => {
      return params['businessUnitId'];
    }),
    distinctUntilChanged(),
  );
  facilityId$ = this._activatedRoute.queryParams.pipe(
    map((params: Params) => {
      return params['facilityId'];
    }),
    distinctUntilChanged(),
  );

  constructor(
    private _activatedRoute: ActivatedRoute,
    @Inject(DOCUMENT) private _document: any,
    private _renderer2: Renderer2,
    private _router: Router,
    private _configService: ConfigService,
    private _mediaWatcherService: MediaWatcherService,
    private _platformService: PlatformService,
    private _loadingBarService: LoadingBarService,
    private _authService: AuthService,
    private _businessUnitService: BusinessUnitService,
    private _tenantService: TenantService,
    private _facilityService: FacilityService,
    private _contextSelectorService: ContextSelectorService,
    private _userService: UserService,
    private _cdr: ChangeDetectorRef,
  ) {
    // Subscribe to NavigationEnd event
    this._router.events.pipe(takeUntil(this._unsubscribeAll)).subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        // Show loading indicator
        this._loadingBarService.show();
      }

      if (event instanceof NavigationEnd) {
        // Hide loading indicator
        this._loadingBarService.hide();
        // Update the layout
        this._updateLayout();
        this._cdr.markForCheck();
      }
    });
  }

  /**
   * On init
   */
  ngOnInit(): void {
    // Set the theme and scheme based on the configuration.
    // `onMediaChange$` (alias breakpoints) đã bỏ khỏi combineLatest cùng lúc gỡ tầng
    // responsive cỡ điện thoại — nó chỉ còn dùng để tính `isScreenSmall` cho thanh
    // horizontal-sidebar, mà thanh đó đã xoá.
    combineLatest([
      this._configService.config$,
      this._mediaWatcherService.onMediaQueryChange$([
        '(prefers-color-scheme: dark)',
        '(prefers-color-scheme: light)',
      ]),
    ])
      .pipe(
        takeUntil(this._unsubscribeAll),
        map(([config, mql]) => {
          const options = {
            scheme: config.scheme,
            theme: config.theme,
          };

          // If the scheme is set to 'auto'...
          if (config.scheme === 'auto') {
            // Decide the scheme using the media query
            options.scheme = mql.breakpoints['(prefers-color-scheme: dark)'] ? 'dark' : 'light';
          }

          return options;
        }),
      )
      .subscribe((options) => {
        // Store the options
        this.scheme = options.scheme;
        this.theme = options.theme;

        // Update the scheme and theme
        this._updateScheme();
        this._updateTheme();
        this._cdr.markForCheck();
      });

    // Subscribe to config changes
    this._configService.config$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config: AppConfig) => {
        // Store the config
        this.config = config;

        // Update the layout
        this._updateLayout();
        this._cdr.markForCheck();
      });

    // Set the OS name
    this._renderer2.addClass(this._document.body, this._platformService.osName);

    if (this._authService.isAuthenticated) {
      this._tenantService.refresh$
        .pipe(takeUntil(this._unsubscribeAll), startWith(true))
        .subscribe((val) => {
          if (val === true && this._authState.isAuthenticated()) {
            this._tenantService.getList().subscribe();
          }
        });
      this._businessUnitService.refresh$
        .pipe(takeUntil(this._unsubscribeAll), startWith(true))
        .subscribe((val) => {
          if (val === true && this._authState.isAuthenticated()) {
            this._businessUnitService.getList().subscribe();
          }
        });

      this._facilityService.refresh$
        .pipe(takeUntil(this._unsubscribeAll), startWith(true))
        .subscribe((val) => {
          if (val === true && this._authState.isAuthenticated()) {
            this._facilityService.getList().subscribe();
          }
        });

      combineLatest([
        this.tenantId$,
        this._tenantService.tenants$,
        this.businessUnitId$,
        this._businessUnitService.businessUnits$,
        this.facilityId$,
        this._facilityService.facilities$,
      ])
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(
          ([
            selectedTenantId,
            tenants,
            selectedBusinessUnitId,
            businessUnits,
            selectedFacilityId,
            facilities,
          ]) => {
            this._businessUnitService.businessUnitsToDisplay = [];
            this._businessUnitService.selectedBusinessUnit = null;
            this._facilityService.selectedFacility = null;
            if (tenants.length > 0) {
              let selectedTenant: undefined | null | ITenant = null;
              let selectedBusinessUnit: undefined | null | IBusinessUnit = null;
              let selectedFacility: undefined | null | IFacility = null;

              if (selectedTenantId == null) {
                selectedTenant = tenants[0];
              }

              if (selectedTenantId != null) {
                selectedTenant = tenants.find((item) => item.id == selectedTenantId);
              }

              this._tenantService.selectedTenant = selectedTenant;

              const businessUnitsToDisplay = businessUnits.filter(
                (item) => item.tenantId == selectedTenant?.id,
              );

              if (businessUnitsToDisplay.length > 0) {
                // "Tất cả đơn vị" là lựa chọn CÓ CHỦ Ý, không phải "chưa chọn". Cả hai vốn đều là
                // `null` (tham số vắng mặt trên URL), nên nhánh dưới luôn coi là chưa chọn rồi tự
                // gán đơn vị đầu tiên: chọn "Tất cả đơn vị", lưu, mở lại thì thấy đơn vị đầu tiên.
                //
                // Phân biệt bằng CỜ RIÊNG trong localStorage chứ KHÔNG bằng mốc trên URL: 25 màn
                // đang đọc thẳng `queryParams['businessUnitId']` và đưa vào bộ lọc/payload, nên
                // một giá trị mốc kiểu 'all' sẽ bị 25 chỗ đó hiểu thành mã đơn vị có thật.
                // Panel bối cảnh chọn NHIỀU đơn vị: URL vắng `businessUnitId` (25 màn đọc nó như một
                // Guid), header mang danh sách — cũng là lựa chọn có chủ ý, không tự gán đơn vị.
                if (
                  selectedBusinessUnitId == null &&
                  (this._contextSelectorService.isAllBusinessUnitsChosen ||
                    this._contextSelectorService.hasMultiBusinessUnits(selectedTenant?.id))
                ) {
                  selectedBusinessUnit = null;
                } else if (selectedBusinessUnitId == null) {
                  // G3: ưu tiên đơn vị mặc định trong claim tổ chức.
                  const defaultBuId = this._userService.defaultBusinessUnitId;
                  if (defaultBuId) {
                    selectedBusinessUnit = businessUnitsToDisplay.find(
                      (item) => item.id == defaultBuId,
                    );
                  }
                  // Không có mặc định mà thấy NHIỀU đơn vị ⇒ tài khoản toàn tenant (chủ tịch,
                  // kiểm soát nội bộ…). Rơi về `businessUnitsToDisplay[0]` là chọn giúp họ một
                  // công ty theo THỨ TỰ CHỮ CÁI: đo trên dev 27/08, `chairman` rơi vào GOLOS
                  // (0 đơn hàng) nên mở ứng dụng ra thấy danh sách rỗng dù tenant có 77 đơn.
                  // Để `undefined` = không gửi `X-BusinessUnit-Context` = hợp nhất cả tenant,
                  // đúng thứ vai trò ấy cần thấy. KHÔNG ghi cờ `allBusinessUnitsChosen`: đó là
                  // lựa chọn của NGƯỜI DÙNG, không phải suy luận của hệ thống.
                  if (!selectedBusinessUnit && businessUnitsToDisplay.length === 1) {
                    selectedBusinessUnit = businessUnitsToDisplay[0];
                  }
                } else {
                  selectedBusinessUnit = businessUnitsToDisplay.find(
                    (item) => item.id == selectedBusinessUnitId,
                  );
                }

                this._businessUnitService.selectedBusinessUnit = selectedBusinessUnit;
              }

              this._businessUnitService.businessUnitsToDisplay = businessUnitsToDisplay;

              // Facility selection (filtered by selected BU)
              const facilitiesToDisplay = facilities.filter(
                (item) => item.businessUnitId == selectedBusinessUnit?.id,
              );
              this._facilityService.facilitiesToDisplay = facilitiesToDisplay;

              if (facilitiesToDisplay.length > 0 && selectedFacilityId != null) {
                selectedFacility = facilitiesToDisplay.find(
                  (item) => item.id == selectedFacilityId,
                );
              } else if (selectedFacilityId == null) {
                selectedFacility = this._pickRestrictedFacility(
                  facilitiesToDisplay,
                  selectedTenant?.id,
                );
              }
              this._facilityService.selectedFacility = selectedFacility;

              // Sync with ContextSelectorService for interceptor (tenant first — it drives the
              // X-Tenant-Id header the backend needs when the JWT carries no tenant_id claim, e.g. SUPER ADMIN).
              this._contextSelectorService.setTenant(selectedTenant?.id || null);
              this._contextSelectorService.setBusinessUnit(selectedBusinessUnit?.id || null);
              if (selectedFacility?.id) {
                this._contextSelectorService.setFacility(selectedFacility.id);
              }

              if (selectedTenantId != selectedTenant?.id) {
                this._router.navigate([], {
                  queryParams: {
                    tenantId: selectedTenant?.id,
                  },
                  queryParamsHandling: 'merge',
                });
              }

              if (selectedBusinessUnitId != selectedBusinessUnit?.id) {
                this._router.navigate([], {
                  queryParams: {
                    businessUnitId: selectedBusinessUnit?.id,
                  },
                  queryParamsHandling: 'merge',
                });
              }

              if (selectedFacilityId != selectedFacility?.id) {
                this._router.navigate([], {
                  queryParams: {
                    facilityId: selectedFacility?.id || null,
                  },
                  queryParamsHandling: 'merge',
                });
              }
            } else {
              // No tenants available (e.g. the database was cleared). Clear any stale selection
              // so the module-rail CTX badge and the request interceptor don't keep showing /
              // sending a phantom tenant that no longer exists.
              this._tenantService.selectedTenant = null;
              this._businessUnitService.businessUnitsToDisplay = [];
              this._facilityService.facilitiesToDisplay = [];
              this._contextSelectorService.setTenant(null);
              this._contextSelectorService.setBusinessUnit(null);
              this._contextSelectorService.setFacility(null);
            }
            this._cdr.markForCheck();
          },
        );
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Tự chọn cơ sở cho tài khoản BỊ GIỚI HẠN theo cơ sở (token có claim `allowed_facilities`).
   *
   * Người chỉ được gán vai trò ở phạm vi cơ sở mà không gửi `X-Facility-Context` thì mọi request
   * đầu tiên đi dưới ngữ cảnh cả đơn vị, trong khi quyền của họ chỉ có ở cơ sở. Chọn sẵn một cơ sở
   * để request đầu tiên đã mang header.
   *
   * - Backend KHÔNG phát claim cơ sở mặc định (`OrgContextClaimBuilder` chỉ phát
   *   `allowed_facilities`), nên lấy cơ sở được phép ĐẦU TIÊN thuộc đơn vị đang chọn.
   * - Không có claim (danh sách rỗng) hoặc là admin ⇒ trả `null`: hành vi cũ giữ nguyên. Admin bỏ
   *   qua giới hạn y như bộ chọn ngữ cảnh (`facilitiesForScope`).
   * - Người dùng CỐ Ý chọn "Tất cả cơ sở" ⇒ tôn trọng, không chọn đè (cờ riêng, cùng lý do với
   *   `isAllBusinessUnitsChosen`).
   */
  private _pickRestrictedFacility(
    facilitiesToDisplay: IFacility[],
    tenantId?: string | null,
  ): IFacility | null {
    if (this._userService.isAdmin) return null;
    const allowed = this._userService.allowedFacilityIds ?? [];
    if (allowed.length === 0) return null;
    if (this._contextSelectorService.isAllFacilitiesChosen) return null;
    // Đang chọn NHIỀU cơ sở qua panel bối cảnh — header mang danh sách, đừng chọn đè một cơ sở.
    if (this._contextSelectorService.hasMultiFacilities(tenantId)) return null;
    return facilitiesToDisplay.find((item) => !!item.id && allowed.includes(item.id)) ?? null;
  }

  /**
   * Update the selected layout
   */
  private _updateLayout(): void {
    // Get the current activated route
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    // 1. Set the layout from the config
    this.layout = this.config?.layout;

    // 2. Get the query parameter from the current route and
    // set the layout and save the layout to the config
    const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout') as Layout;
    if (layoutFromQueryParam) {
      this.layout = layoutFromQueryParam;
      if (this.config) {
        this.config.layout = layoutFromQueryParam;
      }
    }

    // 3. Iterate through the paths and change the layout as we find
    // a config for it.
    //
    // The reason we do this is that there might be empty grouping
    // paths or componentless routes along the path. Because of that,
    // we cannot just assume that the layout configuration will be
    // in the last path's config or in the first path's config.
    //
    // So, we get all the paths that matched starting from root all
    // the way to the current activated route, walk through them one
    // by one and change the layout as we find the layout config. This
    // way, layout configuration can live anywhere within the path and
    // we won't miss it.
    //
    // Also, this will allow overriding the layout in any time so we
    // can have different layouts for different routes.
    const paths = route.pathFromRoot;
    paths.forEach((path) => {
      // Check if there is a 'layout' data
      if (path.routeConfig && path.routeConfig.data && path.routeConfig.data['layout']) {
        // Set the layout
        this.layout = path.routeConfig.data['layout'];
      }
    });
  }

  /**
   * Update the selected scheme
   *
   * @private
   */
  private _updateScheme(): void {
    // Remove class names for all schemes
    this._document.body.classList.remove('light', 'dark');

    // Add class name for the currently selected scheme
    this._document.body.classList.add(this.scheme);
  }

  /**
   * Update the selected theme
   *
   * @private
   */
  private _updateTheme(): void {
    // Find the class name for the previously selected theme and remove it
    this._document.body.classList.forEach((className: string) => {
      if (className.startsWith('theme-')) {
        this._document.body.classList.remove(className, className.split('-')[1]);
      }
    });

    // Add class name for the currently selected theme
    this._document.body.classList.add(this.theme);
  }
}
